import { Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  getDevelopmentSystemPromptPreview,
  DevelopmentAgentFormData,
} from "../../../utils/developmentSystemPrompt";
import type { ToolType } from "../../../types";

type Step2DevelopmentConfigurationProps = {
  agentFormData: DevelopmentAgentFormData;
  onFormDataChange: (data: DevelopmentAgentFormData) => void;
  displayName: string;
};

// Curated tool catalog for development-v2 comercial agents
const DEVELOPMENT_TOOLS: ReadonlyArray<{ value: ToolType; label: string }> = [
  { value: "transferConversation", label: "Transferir Conversación" },
  { value: "getProject", label: "Obtener Proyecto" },
  { value: "searchProjects", label: "Buscar Proyectos" },
  { value: "typeOfProject", label: "Tipo de Proyecto" },
  { value: "scheduleVisit", label: "Agendar Visita" },
  { value: "setAppointmentIntention", label: "Registrar intención de cita" },
  { value: "assignAgent", label: "Asignar Agente" },
  { value: "updateLeadProfile", label: "Actualizar Perfil del Lead" },
  { value: "discardLead", label: "Descartar Lead" },
  { value: "sendMedia", label: "Enviar multimedia" },
];

const Step2DevelopmentConfiguration = ({
  agentFormData,
  onFormDataChange,
  displayName,
}: Step2DevelopmentConfigurationProps) => {
  return (
    <div className="flex flex-col gap-6">
      <p className="mb-2 text-lg font-semibold">Configuración del Agente Comercial (Desarrollos)</p>
      <p className="text-sm text-gray-500">
        Vincula el agente a un proyecto y a un número de WhatsApp después de crearlo, desde la
        lista de agentes.
      </p>

      <div className="flex flex-col items-start gap-6 md:flex-row">
        {/* Left side - form */}
        <div className="flex flex-1 flex-col gap-6">
          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">
              Nombre del agente <span className="text-red-500">*</span>
            </Label>
            <Input
              value={agentFormData.agentName}
              onChange={(e) => onFormDataChange({ ...agentFormData, agentName: e.target.value })}
              placeholder="Ingresa el nombre del agente"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">
              Enlace a la política de tratamiento de datos <span className="text-red-500">*</span>
            </Label>
            <Input
              value={agentFormData.politicaTratamientoDeDatos}
              onChange={(e) =>
                onFormDataChange({
                  ...agentFormData,
                  politicaTratamientoDeDatos: e.target.value,
                })
              }
              placeholder="https://ejemplo.com/politica-de-datos"
              type="url"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">
              Página web <span className="text-red-500">*</span>
            </Label>
            <Input
              value={agentFormData.webPage}
              onChange={(e) => onFormDataChange({ ...agentFormData, webPage: e.target.value })}
              placeholder="https://ejemplo.com"
              type="url"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">Zonas que cubre</Label>
            <Input
              value={agentFormData.coveredAreas}
              onChange={(e) =>
                onFormDataChange({ ...agentFormData, coveredAreas: e.target.value })
              }
              placeholder="Medellín, Bogotá, Cali..."
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">Mensaje de bienvenida</Label>
            <Textarea
              rows={4}
              value={agentFormData.greetingsMessage}
              onChange={(e) =>
                onFormDataChange({ ...agentFormData, greetingsMessage: e.target.value })
              }
              placeholder="¡Hola! Bienvenido a {merchantDisplayName}..."
            />
            <p className="text-xs text-gray-500">
              Puedes usar{" "}
              <code className="rounded bg-gray-100 px-1">{"{nombreAgente}"}</code> y{" "}
              <code className="rounded bg-gray-100 px-1">{"{politicaTratamientoDeDatos}"}</code>{" "}
              como placeholders.
            </p>
          </div>
        </div>

        {/* Right side - preview + tools */}
        <div className="flex flex-1 flex-col gap-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="size-5 text-purple-500" />
              <p className="text-base font-semibold">Vista previa de las instrucciones</p>
            </div>
            <Textarea
              value={getDevelopmentSystemPromptPreview({
                agentFormData,
                displayName,
              })}
              readOnly
              className="h-[80vh] resize-y overflow-y-auto border border-gray-200 bg-gray-50 p-4 font-mono text-sm"
            />
          </div>

          <div>
            <p className="mb-4 text-base font-semibold">Herramientas</p>
            <div className="flex flex-col gap-2">
              {DEVELOPMENT_TOOLS.map((tool) => (
                <div key={tool.value} className="flex items-center gap-2">
                  <Checkbox
                    checked={agentFormData.selectedTools.includes(tool.value)}
                    onCheckedChange={(checked) => {
                      const currentTools = agentFormData.selectedTools;
                      const newTools = checked
                        ? [...currentTools, tool.value]
                        : currentTools.filter((t) => t !== tool.value);
                      onFormDataChange({ ...agentFormData, selectedTools: newTools });
                    }}
                  />
                  <label className="text-sm">{tool.label}</label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step2DevelopmentConfiguration;
